import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  TextField,
  IconButton,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import SettingsIcon from '@mui/icons-material/Settings';
import { useChatStore } from '@/stores/useChatStore';
import { useStreamingChat } from '@/hooks/useStreamingChat';
import { useSessionStore } from '@/stores/useSessionStore';
import type { ModelLabel } from '@/types/session';

const INPUT_HEIGHT = 72;

interface MessageInputProps {
  target?: ModelLabel | 'both';
  compact?: boolean;
}

/**
 * 底部输入区（72px 高）。
 * 多行 Textarea，Enter 发送，Shift+Enter 换行，左侧配置快捷入口。
 */
const MessageInput: React.FC<MessageInputProps> = ({ target = 'both', compact = false }) => {
  const navigate = useNavigate();
  const [text, setText] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const currentSession = useSessionStore((s) => s.currentSession);
  const isStreamingLeft = useChatStore((s) => s.isStreamingLeft);
  const isStreamingRight = useChatStore((s) => s.isStreamingRight);
  const sendMessageToStore = useChatStore((s) => s.sendMessage);

  const isStreaming =
    target === 'left' ? isStreamingLeft : target === 'right' ? isStreamingRight : isStreamingLeft || isStreamingRight;

  const { startStream } = useStreamingChat(
    currentSession?.session_id || null
  );

  const handleSend = () => {
    const trimmed = text.trim();
    if (!trimmed || isStreaming || !currentSession) return;

    sendMessageToStore(trimmed, target);
    setText('');

    // 启动 SSE 流
    startStream(trimmed, target);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setText(e.target.value);
  };

  return (
    <Box
      sx={{
        height: compact ? 64 : INPUT_HEIGHT,
        minHeight: compact ? 64 : INPUT_HEIGHT,
        px: compact ? 1.5 : 2,
        py: 1,
        borderTop: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
      }}
    >
      {!compact && (
        <Tooltip title="打开配置">
          <IconButton
            size="small"
            onClick={() => navigate('/config')}
            sx={{ color: 'text.secondary' }}
          >
            <SettingsIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}

      {/* 输入框 */}
      <TextField
        inputRef={textareaRef}
        fullWidth
        multiline
        maxRows={3}
        placeholder={
          isStreaming
            ? '正在生成...'
            : currentSession
            ? target === 'both'
              ? '输入消息，Enter 发送，Shift+Enter 换行'
              : '继续追问这一侧...'
            : '请先创建会话'
        }
        value={text}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        disabled={isStreaming || !currentSession}
        variant="outlined"
        size="small"
        sx={{
          '& .MuiOutlinedInput-root': {
            bgcolor: 'background.default',
            borderRadius: 2,
          },
        }}
      />

      {/* 发送按钮 */}
      <IconButton
        color="primary"
        onClick={handleSend}
        disabled={isStreaming || !text.trim() || !currentSession}
        sx={{
          width: 40,
          height: 40,
          flexShrink: 0,
        }}
      >
        {isStreaming ? (
          <CircularProgress size={20} color="inherit" />
        ) : (
          <SendIcon />
        )}
      </IconButton>
    </Box>
  );
};

export default MessageInput;
